/**
 * Opt-in normalized reads; the v1 `useLazyGet*StopsQuery` hooks still hand back raw BE envelopes, so a
 * migrating call site imports from here and nothing forces the rest of them to move with it.
 */
import { format } from 'date-fns'
import { useCallback } from 'react'

import { useLazyGetDailyStopsQuery } from 'api/route/dailyRoute'
import { useLazyGetMasterStopsQuery } from 'api/route/masterRoute'
import { fromDailyStopListApi, fromMasterStopListApi } from 'types/adapters/route'

import type { GetDailyStopsResponse } from 'types/api/route/dailyStop'
import type { GetMasterStopsResponse } from 'types/api/route/masterStop'
import type { WeekDay } from 'types/entities/shared'
import type {
  DailyStop,
  GetDailyStopsArgs,
  GetMasterStopsArgs,
  MasterStop,
  MutationPromise,
  OnRequestStop,
  RoutableMasterStop,
} from 'types/modules/routing'

const weekDayFor = (serviceDateIso: string): WeekDay | null => {
  const parsed = new Date(serviceDateIso)
  if (Number.isNaN(parsed.getTime())) return null
  return format(parsed, 'EEEE') as WeekDay
}

/**
 * Pure adapter: raw `getDailyStops` response → `ReadonlyArray<DailyStop | OnRequestStop>`.
 * Exported so the v2 hook (which uses `dispatch(api.endpoints.getDailyStops.initiate)`
 * to bypass the RTK cache) can reuse the same boundary logic.
 */
export const adaptDailyResponse = (args: GetDailyStopsArgs, response: GetDailyStopsResponse): ReadonlyArray<DailyStop | OnRequestStop> =>
  fromDailyStopListApi(response?.stops ?? [], { dayOfWeek: weekDayFor(args.serviceDate) })

/** Pure adapter: raw `getMasterStops` response → `ReadonlyArray<MasterStop | RoutableMasterStop>`. */
export const adaptMasterResponse = (response: GetMasterStopsResponse): ReadonlyArray<MasterStop | RoutableMasterStop> =>
  fromMasterStopListApi(response?.stops ?? [])

const wrapPromise = <Raw, Adapted>(raw: MutationPromise<Raw>, adapt: (value: Raw) => Adapted): MutationPromise<Adapted> => {
  const wrapped = raw.then(adapt) as MutationPromise<Adapted>
  wrapped.unwrap = async () => adapt(await raw.unwrap())
  return wrapped
}

export type LazyNormalizedDailyStopsTrigger = (args: GetDailyStopsArgs) => MutationPromise<ReadonlyArray<DailyStop | OnRequestStop>>

export type LazyNormalizedMasterStopsTrigger = (args: GetMasterStopsArgs) => MutationPromise<ReadonlyArray<MasterStop | RoutableMasterStop>>

/**
 * Lazy query for `getDailyStops` that resolves to post-adapter
 * `ReadonlyArray<DailyStop | OnRequestStop>`. Mirrors the
 * `useLazyGetDailyStopsQuery` tuple shape — only the trigger is wrapped.
 */
export const useLazyNormalizedDailyStops = (): readonly [
  LazyNormalizedDailyStopsTrigger,
  ReturnType<typeof useLazyGetDailyStopsQuery>[1]
] => {
  const [trigger, state] = useLazyGetDailyStopsQuery()
  const wrapped = useCallback<LazyNormalizedDailyStopsTrigger>(
    args => wrapPromise(trigger(args) as unknown as MutationPromise<GetDailyStopsResponse>, response => adaptDailyResponse(args, response)),
    [trigger]
  )
  return [wrapped, state]
}

/** Same shape as `useLazyNormalizedDailyStops` for the master query. */
export const useLazyNormalizedMasterStops = (): readonly [
  LazyNormalizedMasterStopsTrigger,
  ReturnType<typeof useLazyGetMasterStopsQuery>[1]
] => {
  const [trigger, state] = useLazyGetMasterStopsQuery()
  const wrapped = useCallback<LazyNormalizedMasterStopsTrigger>(
    args => wrapPromise(trigger(args) as unknown as MutationPromise<GetMasterStopsResponse>, adaptMasterResponse),
    [trigger]
  )
  return [wrapped, state]
}
